import { useState, useRef, useCallback, type ReactNode } from 'react';
import type { Tweaks, Accent, Density, Direction } from '../types';

interface Props {
  tweaks: Tweaks;
  onSet: <K extends keyof Tweaks>(key: K, value: Tweaks[K]) => void;
}

const ACCENT_OPTIONS: { value: Accent; colors: [string, string] }[] = [
  { value: 'blue',   colors: ['#2D00F7', '#8900F2'] },
  { value: 'purple', colors: ['#6739FD', '#A855F7'] },
  { value: 'teal',   colors: ['#0D9488', '#5EEAD4'] },
  { value: 'ink',    colors: ['#1A1F36', '#64748B'] },
];

export function TweaksPanel({ tweaks, onSet }: Props) {
  const [open, setOpen] = useState(false);
  const dragRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef({ x: 80, y: 80 });

  const clamp = useCallback(() => {
    const panel = dragRef.current;
    if (!panel) return;
    const w = panel.offsetWidth, h = panel.offsetHeight;
    offsetRef.current = {
      x: Math.max(16, Math.min(window.innerWidth - w - 16, offsetRef.current.x)),
      y: Math.max(16, Math.min(window.innerHeight - h - 16, offsetRef.current.y)),
    };
    panel.style.right = offsetRef.current.x + 'px';
    panel.style.bottom = offsetRef.current.y + 'px';
  }, []);

  const onDragStart = (e: React.MouseEvent) => {
    const panel = dragRef.current;
    if (!panel) return;
    const r = panel.getBoundingClientRect();
    const [sx, sy] = [e.clientX, e.clientY];
    const [startR, startB] = [window.innerWidth - r.right, window.innerHeight - r.bottom];
    const move = (ev: MouseEvent) => {
      offsetRef.current = { x: startR - (ev.clientX - sx), y: startB - (ev.clientY - sy) };
      clamp();
    };
    const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="twk-panel"
        title="Open tweaks"
        style={{
          position: 'fixed',
          right: 16,
          bottom: 60,
          zIndex: 2147483645,
          background: 'rgba(250,249,247,.88)',
          border: '.5px solid rgba(255,255,255,.6)',
          borderRadius: 12,
          padding: '8px 14px',
          fontSize: 12,
          fontWeight: 600,
          color: '#29261b',
          cursor: 'pointer',
          fontFamily: 'inherit',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 4px 16px rgba(0,0,0,.14)',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span>⚙</span> Tweaks
      </button>
    );
  }

  return (
    <div
      ref={dragRef}
      className="twk-panel"
      style={{
        position: 'fixed',
        right: offsetRef.current.x,
        bottom: offsetRef.current.y,
        zIndex: 2147483646,
        width: 280,
        maxHeight: 'calc(100vh - 32px)',
        display: 'flex',
        flexDirection: 'column',
        transform: 'none',
        background: 'rgba(250,249,247,.92)',
        backdropFilter: 'blur(24px) saturate(160%)',
        border: '.5px solid rgba(255,255,255,.6)',
        borderRadius: 14,
        boxShadow: '0 1px 0 rgba(255,255,255,.5) inset, 0 12px 40px rgba(0,0,0,.18)',
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, sans-serif',
        fontSize: 11.5,
        color: '#29261b',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        onMouseDown={onDragStart}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 8px 10px 14px',
          cursor: 'move',
          userSelect: 'none',
        }}
      >
        <strong style={{ fontSize: 12, fontWeight: 600 }}>Tweaks</strong>
        <button
          onClick={() => setOpen(false)}
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            appearance: 'none',
            border: 0,
            background: 'transparent',
            color: 'rgba(41,38,27,.55)',
            width: 22,
            height: 22,
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 13,
            lineHeight: 1,
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          ✕
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: '2px 14px 14px', display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto' }}>

        {/* Era */}
        <Section label="Era">
          <SegmentRow
            value={tweaks.theme}
            options={[
              { label: '2026', value: 'modern' },
              { label: '1997', value: '90s' },
            ]}
            onChange={(v) => onSet('theme', v as Tweaks['theme'])}
          />
        </Section>

        {/* Direction */}
        <Section label="Visual direction">
          <SegmentRow
            value={tweaks.direction}
            options={[
              { label: 'Soft', value: 'A' as Direction },
              { label: 'Bold', value: 'B' as Direction },
            ]}
            onChange={(v) => onSet('direction', v as Direction)}
          />
        </Section>

        {/* Accent */}
        <Section label="Accent">
          <div style={{ display: 'flex', gap: 6 }}>
            {ACCENT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => onSet('accent', opt.value)}
                title={opt.value}
                style={{
                  flex: 1,
                  height: 46,
                  border: 'none',
                  borderRadius: 6,
                  background: `linear-gradient(135deg, ${opt.colors[0]} 0%, ${opt.colors[1]} 100%)`,
                  cursor: 'pointer',
                  boxShadow: tweaks.accent === opt.value
                    ? '0 0 0 1.5px rgba(0,0,0,.85), 0 2px 6px rgba(0,0,0,.15)'
                    : '0 0 0 .5px rgba(0,0,0,.12), 0 1px 2px rgba(0,0,0,.06)',
                  padding: 0,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {tweaks.accent === opt.value && (
                  <svg width="13" height="13" viewBox="0 0 14 14">
                    <path d="M3 7.2 5.8 10 11 4.2" fill="none" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" stroke="#fff" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </Section>

        {/* Density */}
        <Section label="Density">
          <SegmentRow
            value={tweaks.density}
            options={[
              { label: 'Compact', value: 'compact' as Density },
              { label: 'Regular', value: 'regular' as Density },
              { label: 'Comfy',   value: 'comfy'   as Density },
            ]}
            onChange={(v) => onSet('density', v as Density)}
          />
        </Section>

        {/* Privacy */}
        <Section label="Privacy">
          <ToggleRow
            label="Preview as others"
            value={tweaks.previewMode}
            onChange={(v) => onSet('previewMode', v)}
          />
        </Section>
      </div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'rgba(41,38,27,.45)', paddingTop: 10 }}>
        {label}
      </div>
      {children}
    </>
  );
}

function SegmentRow({ value, options, onChange }: {
  value: string;
  options: { label: string; value: string }[];
  onChange: (v: string) => void;
}) {
  const idx = options.findIndex((o) => o.value === value);
  const n = options.length;

  return (
    <div
      role="radiogroup"
      onClick={(e) => {
        const btn = (e.target as HTMLElement).closest('button');
        if (btn?.dataset.value !== undefined) onChange(btn.dataset.value);
      }}
      style={{
        position: 'relative',
        display: 'flex',
        padding: 2,
        borderRadius: 8,
        background: 'rgba(0,0,0,.06)',
        userSelect: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 2,
          bottom: 2,
          left: `calc(2px + ${idx} * (100% - 4px) / ${n})`,
          width: `calc((100% - 4px) / ${n})`,
          borderRadius: 6,
          background: 'rgba(255,255,255,.9)',
          boxShadow: '0 1px 2px rgba(0,0,0,.12)',
          transition: 'left .15s cubic-bezier(.3,.7,.4,1)',
        }}
      />
      {options.map((o) => (
        <button
          key={o.value}
          data-value={o.value}
          role="radio"
          aria-checked={o.value === value}
          style={{
            appearance: 'none',
            position: 'relative',
            zIndex: 1,
            flex: 1,
            border: 0,
            background: 'transparent',
            color: 'inherit',
            fontFamily: 'inherit',
            fontWeight: 500,
            minHeight: 22,
            borderRadius: 6,
            cursor: 'pointer',
            padding: '4px 6px',
            fontSize: 11.5,
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ToggleRow({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
      <div style={{ color: 'rgba(41,38,27,.72)', fontWeight: 500, fontSize: 11.5 }}>{label}</div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        style={{
          appearance: 'none',
          position: 'relative',
          width: 32,
          height: 18,
          border: 0,
          borderRadius: 999,
          background: value ? '#34c759' : 'rgba(0,0,0,.15)',
          transition: 'background .15s',
          cursor: 'pointer',
          padding: 0,
          flexShrink: 0,
        }}
      >
        <i
          style={{
            position: 'absolute',
            top: 2,
            left: 2,
            width: 14,
            height: 14,
            borderRadius: '50%',
            background: '#fff',
            boxShadow: '0 1px 2px rgba(0,0,0,.25)',
            transition: 'transform .15s',
            transform: value ? 'translateX(14px)' : 'none',
            display: 'block',
          }}
        />
      </button>
    </div>
  );
}
